import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { first, finalize, Subscription } from 'rxjs';
import { User } from '@shared/entities/user';
import { Language } from '@shared/entities/language';
import { SelectOptionType, Roles } from '@shared/models/types';
import { UserFormService } from '@shared/services/api/user-form.service';
import { UserWelcomeMessageService } from '@shared/services/api/user-welcome-message.service';
import { LanguageService } from '@shared/services/language.service';
import { ThemeService } from 'src/app/services/theme.service';
import { ModalService } from '@shared/services/modal.service';

const DEFAULT_WELCOME_MESSAGE_LANGUAGE = 'it';

@Component({
  selector: 'app-create-admin-modal',
  templateUrl: './create-admin-modal.component.html',
  styleUrls: ['./create-admin-modal.component.scss']
})
export class CreateAdminModalComponent implements OnInit, OnDestroy {
  @Input() showClose = true;
  @Input() showBtns = true;
  form!: FormGroup;
  isLight = true;
  isLoading = false;
  isLoadingTemplate = false;
  submitError = '';

  welcomeMessageTemplate = '';
  welcomeMessageSubject = '';
  availableLanguages: Language[] = [];

  availablePermissions: SelectOptionType[] = [];

  private templateRequestSubscription?: Subscription;
  private themeSubscription?: Subscription;

  constructor(
    private modalService: ModalService,
    private formBuilder: FormBuilder,
    private userFormService: UserFormService,
    private welcomeMessageService: UserWelcomeMessageService,
    private languageService: LanguageService,
    private themeService: ThemeService,
    public translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadAvailableLanguages();
    this.populateAvailablePermissions();

    this.themeSubscription = this.themeService.isLight$.subscribe((isLight: boolean) => {
      this.isLight = isLight;
    });
  }

  ngOnDestroy(): void {
    if (this.templateRequestSubscription) {
      this.templateRequestSubscription.unsubscribe();
    }
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
  }

  private initializeForm(): void {
    this.form = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      mobilePhone: [''],
      password: ['', [Validators.required, Validators.minLength(8)]],
      permissions: [[]],
      welcomeSubject: ['', Validators.required],
      templateMail: ['', Validators.required],
      templateLanguage: ['']
    });
  }

  private populateAvailablePermissions(): void {
    this.availablePermissions = [
      { id: 'home', value: 'SIDEBAR.HOME' },
      { id: 'ticket', value: 'SIDEBAR.TICKET' },
      { id: 'request', value: 'SIDEBAR.REQUEST' },
      { id: 'repository', value: 'SIDEBAR.REPOSITORY' },
      { id: 'learning', value: 'SIDEBAR.LEARNING' },
      { id: 'analytics', value: 'SIDEBAR.ANALYTICS' },
      { id: 'knowledge-base', value: 'SIDEBAR.KNOWLEDGE_BASE' },
      { id: 'shopify', value: 'SIDEBAR.SHOPIFY' }
    ];

    const allPermissionsExceptFinalUsers = this.availablePermissions
      .filter((permission) => permission.id !== 'final-users')
      .map((permission) => permission.id.toString());

    this.form.patchValue({ permissions: allPermissionsExceptFinalUsers });
  }

  private loadAvailableLanguages(): void {
    this.languageService
      .getLanguages()
      .pipe(first())
      .subscribe({
        next: (response: any) => {
          if (response && response.languages) {
            this.availableLanguages = response.languages;
            if (this.availableLanguages.length > 0) {
              const defaultLang =
                this.availableLanguages.find((l: Language) => l.lang === DEFAULT_WELCOME_MESSAGE_LANGUAGE)?.lang ||
                this.availableLanguages[0].lang ||
                '';
              this.form.patchValue({ templateLanguage: defaultLang });
              this.loadWelcomeMessageTemplate();
            }
          }
        },
        error: (err: any) => {
          console.error('Error loading available languages:', err);
        }
      });
  }

  private loadWelcomeMessageTemplate(): void {
    const lang = this.form.get('templateLanguage')?.value;

    if (this.templateRequestSubscription) {
      this.templateRequestSubscription.unsubscribe();
      this.templateRequestSubscription = undefined;
    }

    this.isLoadingTemplate = true;

    const sub = this.welcomeMessageService
      .getWelcomeMessage(lang)
      .pipe(
        first(),
        finalize(() => {
          this.isLoadingTemplate = false;
        })
      )
      .subscribe({
        next: (welcomeMessage: any) => {
          if (welcomeMessage) {
            this.welcomeMessageTemplate = welcomeMessage.message || '';
            this.welcomeMessageSubject = welcomeMessage.subject || '';
            this.form.patchValue({
              templateMail: this.welcomeMessageTemplate,
              welcomeSubject: this.welcomeMessageSubject
            });
          }
        },
        error: (err: any) => {
          console.error('Error loading welcome message template:', err);
        }
      });

    this.templateRequestSubscription = sub;
  }

  onTemplateLanguageChange(): void {
    this.loadWelcomeMessageTemplate();
  }

  onPermissionSelection(permissionId: string, isChecked: boolean): void {
    const currentPermissions = this.form.get('permissions')?.value || [];

    if (isChecked) {
      if (!currentPermissions.includes(permissionId)) {
        this.form.patchValue({
          permissions: [...currentPermissions, permissionId]
        });
      }
    } else {
      this.form.patchValue({
        permissions: currentPermissions.filter((p: string) => p !== permissionId)
      });
    }
  }

  isPermissionSelected(permissionId: string): boolean {
    const permissions = this.form.get('permissions')?.value || [];
    return permissions.includes(permissionId);
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.markAllFieldsAsTouched();
      return;
    }

    this.isLoading = true;
    const formData = this.form.getRawValue();

    const userData: User = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      mobilePhone: formData.mobilePhone,
      password: formData.password,
      role: Roles.ADMIN,
      status: 'ACTIVE',
      permissions: formData.permissions,
      machines: [],
      accounts: [],
      sfContactId: '',
      templateMail: {
        subject: formData.welcomeSubject || '',
        message: formData.templateMail || ''
      }
    };

    this.userFormService
      .saveUser(userData)
      .pipe(first())
      .subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response && (response.error || response.validation)) {
            this.submitError = this.mapErrorMessage(response.error?.message);
          } else {
            this.submitError = '';
            this.modalService.close({ success: true, data: response });
          }
        },
        error: (err) => {
          this.isLoading = false;
          this.submitError = this.mapErrorMessage(err?.error?.message);
        }
      });
  }

  private markAllFieldsAsTouched(): void {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.markAsTouched();
    });
  }

  close(): void {
    this.modalService.close('cancel');
  }

  private mapErrorMessage(backendMessage: string | undefined): string {
    if (!backendMessage) return 'USER_FORM.ERROR_GENERIC';
    if (backendMessage.includes('already exists')) return 'USER_FORM.ERROR_EMAIL_EXISTS';
    if (backendMessage.includes('not valid')) return 'USER_FORM.ERROR_EMAIL_INVALID';
    if (backendMessage.includes('6 characters')) return 'USER_FORM.ERROR_PASSWORD_INVALID';
    return 'USER_FORM.ERROR_GENERIC';
  }

  onModalClose(): void {
    this.close();
  }
}
